<template>
  <template v-if="formConfig?.fields">
    <mpf-form
      :form-title="populatedForm.config?.formTitle"
      :hint="populatedForm.config?.hint"
      :fields="populatedForm?.fields"
      :form-errors="formErrors"
      @submit="(e) => handleSubmit(e)"
    />

    <div class="mpf-u-margin-top-4">
      <mpf-button
        prevent-double-click
        label="Resend verification code"
        variant="secondary"
        @click="handleResendSubmit()"
        :disabled="apiLoading"
      />
    </div>
  </template>
</template>
<script setup lang="ts">
  import MpfForm from '../components/MpfForm/MpfForm.vue';
  import { useRouteMeta } from '../composables/useRouteMeta';
  import { MpfFormPropsWithConfig } from '../../types/router-meta';
  import { router } from '../../router';

  import { useRepeatPrescriptionRequestStore } from '../../stores/useRepeatPrescriptionRequestStore';
  import { useUrlSegments } from '../composables/useUrlSegments';
  import { VerifyPatientResult } from '../../types';
  import { computed, ref } from 'vue';
  import { useMergeDataIntoForm } from '../composables/useMergeDataIntoForm';
  import MpfButton from '../components/MpfButton/MpfButton.vue';
  import { storeToRefs } from 'pinia';
  import { useErrorStore } from '../../stores/useErrorStore';

  const { formConfig } = useRouteMeta<MpfFormPropsWithConfig>();
  const { firstSegment } = useUrlSegments();
  const repeatPrescriptionRequestStore = useRepeatPrescriptionRequestStore();
  const { apiLoading } = storeToRefs(repeatPrescriptionRequestStore);
  const { mergeDataIntoForm } = useMergeDataIntoForm();
  const errorStore = useErrorStore();

  type FormError = {
    name: string;
    errors: string[];
  };

  const formErrors = ref<FormError[]>([]);

  const handleSubmit = async (data: any) => {
    if (!data.valid) return;
    // NB  2FA only used for repeat prescriptions and bypassed when using NHS login
    const result: VerifyPatientResult | null = await repeatPrescriptionRequestStore.verifyPatientAndCode(
      data.values.verificationCode,
    );
    if (!result) return;
    if (result.ok) {
      await router.push(`/${firstSegment.value}/prescriptions-list`);
    } else if (!result.ok) {
      errorStore.setErrors(result.errors);
      const error = errorStore.getFirstMessage();
      formErrors.value = [
        {
          name: 'verificationCode',
          errors: [error && error.trim() ? error : 'The code is not valid'],
        },
      ];
    } else {
      errorStore.setErrors(result.errors);
      formErrors.value = [{ name: 'verificationCode', errors: [...result.errors] }];
    }
  };

  const populatedForm = computed(() => {
    const config = formConfig.value as MpfFormPropsWithConfig | undefined;
    if (!config) return null;
    return mergeDataIntoForm(config, `${firstSegment.value}`) ?? config;
  });

  const handleResendSubmit = async () => {
    const result = await repeatPrescriptionRequestStore.send2FAVerificationCode();
    if (!result) return;
    if (result.ok) {
      console.log('Code resent');
    } else {
      formErrors.value = [{ name: 'verificationCode', errors: ['Code could not be resent'] }];
      console.error('Something went wrong. Please try again later.');
    }
  };
</script>
